import { useState, useEffect, useCallback, Fragment } from 'react'
import { orgApi } from '../api'
import { useEscapeKey } from '../useEscapeKey'
import { Pill } from './Pill'
import { enumLabel, useI18n, type MessageKey } from '../i18n'
import type { Organization, OrgPlan, OrgStats } from '../types'

/** Noms commerciaux des formules : ils ne se traduisent pas. */
const PLAN_LABELS: Record<OrgPlan, string> = { FREE: 'Free', PRO: 'Pro', ENTERPRISE: 'Enterprise' }
const PLAN_VARIANTS: Record<OrgPlan, 'info' | 'warning' | 'critical'> = { FREE: 'info', PRO: 'warning', ENTERPRISE: 'critical' }

/**
 * Modules abonnables. Les clés sont celles du serveur (`MODULE_KEYS`) et de
 * `allowedTabs` : un libellé change sans conséquence, une clé non.
 */
const ALL_MODULES: Array<{ key: string; labelKey: MessageKey }> = [
  { key: 'merchants', labelKey: 'module.merchants' },
  { key: 'discovery', labelKey: 'module.discovery' },
  { key: 'webscan', labelKey: 'module.webscan' },
  { key: 'ads', labelKey: 'module.ads' },
  { key: 'betting', labelKey: 'module.betting' },
  { key: 'brands', labelKey: 'module.brands' },
  { key: 'rules', labelKey: 'module.rules' },
  { key: 'users', labelKey: 'module.users' },
  { key: 'orgs', labelKey: 'module.orgs' },
]

const ALL_COUNTRY_CODES = ['SN', 'CI', 'ML', 'BF', 'GN', 'TG', 'BJ', 'NE', 'CM', 'GA', 'GH']

const EMPTY_FORM = {
  name: '',
  slug: '',
  countries: [] as string[],
  modules: [] as string[],
  plan: 'FREE' as OrgPlan,
  maxUsers: 2,
  maxCountries: 1,
  contactEmail: '',
  contactName: '',
}

export function OrgManager() {
  const { t, fmt } = useI18n()
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null)
  const [orgStats, setOrgStats] = useState<Record<string, OrgStats>>({})

  const load = useCallback(async () => {
    try {
      const data = await orgApi.findAll()
      setOrgs(data)
    } catch {
      setError(t('orgs.loadError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load])

  const loadOrgStats = useCallback(async (orgId: string) => {
    try {
      const stats = await orgApi.getOrgStats(orgId)
      setOrgStats(prev => ({ ...prev, [orgId]: stats }))
    } catch {}
  }, [])

  const toggleExpand = (orgId: string) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null)
    } else {
      setExpandedOrg(orgId)
      if (!orgStats[orgId]) loadOrgStats(orgId)
    }
  }

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM)
    setEditId(null)
    setShowForm(false)
    setError(null)
  }, [])

  useEscapeKey(showForm, resetForm)

  const startEdit = (org: Organization) => {
    setForm({
      name: org.name,
      slug: org.slug,
      countries: org.countries,
      modules: org.modules ?? [],
      plan: org.plan,
      maxUsers: org.maxUsers,
      maxCountries: org.maxCountries,
      contactEmail: org.contactEmail,
      contactName: org.contactName ?? '',
    })
    setEditId(org.id)
    setShowForm(true)
    setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      if (editId) {
        await orgApi.update(editId, form)
      } else {
        if (!form.name || !form.slug || !form.contactEmail) {
          setError(t('orgs.requiredFields'))
          return
        }
        if (form.modules.length === 0) {
          setError(t('orgs.moduleRequired'))
          return
        }
        if (form.countries.length === 0) {
          setError(t('orgs.countryRequired'))
          return
        }
        await orgApi.create(form)
      }
      resetForm()
      load()
    } catch (err: any) {
      setError(err.message ?? t('users.genericError'))
    }
  }

  const toggleActive = async (org: Organization) => {
    try {
      await orgApi.update(org.id, { isActive: !org.isActive })
      load()
    } catch (err: any) {
      setError(err.message ?? t('users.genericError'))
    }
  }

  const handleDelete = async (org: Organization) => {
    if (!confirm(t('orgs.deleteConfirm', { name: org.name }))) return
    try {
      await orgApi.remove(org.id)
      load()
    } catch (err: any) {
      setError(err.message ?? t('users.genericError'))
    }
  }

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
  }

  if (loading) return <p>{t('common.loading')}</p>

  return (
    <div className="section-card">
      <div className="section-header">
        <h2>{t('orgs.title')}</h2>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true) }}>
          {t('orgs.newOrg')}
        </button>
      </div>

      {error && !showForm && <div className="alert-error">{error}</div>}

      {showForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h3>{editId ? t('orgs.formEdit') : t('orgs.formNew')}</h3>
                <button type="button" className="modal-close" onClick={resetForm} aria-label={t('common.close')}>&times;</button>
              </div>
              <div className="modal-body">
                {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}
                <div className="user-form-grid">
                  <label>
                    {t('common.name')}
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => {
                        const name = e.target.value
                        setForm(f => ({
                          ...f,
                          name,
                          ...(!editId ? { slug: generateSlug(name) } : {}),
                        }))
                      }}
                      required
                      autoFocus
                    />
                  </label>
                  <label>
                    {t('orgs.slug')}
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
                      required
                      pattern="[a-z0-9-]+"
                      title={t('orgs.slugTitle')}
                    />
                  </label>
                  <label>
                    {t('orgs.contactEmail')}
                    <input
                      type="email"
                      value={form.contactEmail}
                      onChange={(e) => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                      required
                    />
                  </label>
                  <label>
                    {t('orgs.contactName')}
                    <input
                      type="text"
                      value={form.contactName}
                      onChange={(e) => setForm(f => ({ ...f, contactName: e.target.value }))}
                    />
                  </label>
                  <label>
                    {t('orgs.plan')}
                    <select
                      value={form.plan}
                      onChange={(e) => setForm(f => ({ ...f, plan: e.target.value as OrgPlan }))}
                    >
                      <option value="FREE">Free</option>
                      <option value="PRO">Pro</option>
                      <option value="ENTERPRISE">Enterprise</option>
                    </select>
                  </label>
                  <label>
                    {t('orgs.maxUsers')}
                    <input
                      type="number"
                      min={1}
                      value={form.maxUsers}
                      onChange={(e) => setForm(f => ({ ...f, maxUsers: Number(e.target.value) }))}
                    />
                  </label>
                  <label>
                    {t('orgs.maxCountries')}
                    <input
                      type="number"
                      min={1}
                      value={form.maxCountries}
                      onChange={(e) => setForm(f => ({ ...f, maxCountries: Number(e.target.value) }))}
                    />
                  </label>
                </div>

                <div className="user-permissions">
                  <div className="user-perm-group">
                    <label className="user-perm-label">
                      {t('orgs.assignedCountries')}
                      <span className="user-perm-hint">{t('orgs.countriesCount', { n: form.countries.length, max: form.maxCountries })}</span>
                    </label>
                    <div className="user-perm-checks">
                      {ALL_COUNTRY_CODES.map(code => (
                        <label key={code} className="user-perm-check">
                          <input
                            type="checkbox"
                            checked={form.countries.includes(code)}
                            onChange={(e) => setForm(f => ({
                              ...f,
                              countries: e.target.checked
                                ? [...f.countries, code]
                                : f.countries.filter(x => x !== code),
                            }))}
                          />
                          {enumLabel(t, 'country', code, code)}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="user-perm-group">
                    <label className="user-perm-label">
                      {t('orgs.openModules')}
                      <span className="user-perm-hint">{t('orgs.modulesCount', { n: form.modules.length, max: ALL_MODULES.length })}</span>
                    </label>
                    <p className="user-perm-note">
                      {t('orgs.modulesNoteA')} <strong>{t('orgs.modulesNoteAnd')}</strong> {t('orgs.modulesNoteB')}
                    </p>
                    <div className="user-perm-checks">
                      {ALL_MODULES.map(m => (
                        <label key={m.key} className="user-perm-check">
                          <input
                            type="checkbox"
                            checked={form.modules.includes(m.key)}
                            onChange={(e) => setForm(f => ({
                              ...f,
                              modules: e.target.checked
                                ? [...f.modules, m.key]
                                : f.modules.filter(x => x !== m.key),
                            }))}
                          />
                          {t(m.labelKey)}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {editId ? t('common.save') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">{t('common.organization')}</th>
            <th scope="col">{t('orgs.slug')}</th>
            <th scope="col">{t('orgs.plan')}</th>
            <th scope="col">{t('common.countries')}</th>
            <th scope="col">{t('common.modules')}</th>
            <th scope="col">{t('orgs.colLimits')}</th>
            <th scope="col">{t('orgs.colContact')}</th>
            <th scope="col">{t('common.status')}</th>
            <th scope="col">{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((org) => (
            <Fragment key={org.id}>
              <tr className={org.isActive ? '' : 'user-inactive'} style={{ cursor: 'pointer' }} onClick={() => toggleExpand(org.id)}>
                <td><strong>{org.name}</strong></td>
                <td><code>{org.slug}</code></td>
                <td><Pill label={PLAN_LABELS[org.plan]} variant={PLAN_VARIANTS[org.plan]} /></td>
                <td>
                  <div className="user-perm-tags">
                    {org.countries.map(c => (
                      <span key={c} className="user-perm-tag">{c}</span>
                    ))}
                  </div>
                </td>
                <td>
                  {(org.modules ?? []).length === 0 ? (
                    <span className="user-perm-none">{t('orgs.noModules')}</span>
                  ) : (
                    <div className="user-perm-tags">
                      {(org.modules ?? []).map(m => (
                        <span key={m} className="user-perm-tag">
                          {(() => {
                            const found = ALL_MODULES.find(x => x.key === m)
                            return found ? t(found.labelKey) : m
                          })()}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td>{t('orgs.limits', { users: org.maxUsers, countries: org.maxCountries })}</td>
                <td>{org.contactEmail}</td>
                <td>
                  <Pill
                    label={org.isActive ? t('users.active') : t('orgs.inactive')}
                    variant={org.isActive ? 'ok' : 'warning'}
                  />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button className="btn-icon" title={t('common.edit')} aria-label={t('users.editAria', { name: org.name })} onClick={() => startEdit(org)}>✏️</button>
                  <button
                    className="btn-icon"
                    title={org.isActive ? t('users.deactivate') : t('users.activate')}
                    aria-label={t('users.toggleAria', { action: org.isActive ? t('users.deactivate') : t('users.activate'), name: org.name })}
                    onClick={() => toggleActive(org)}
                  >
                    {org.isActive ? '🚫' : '✅'}
                  </button>
                  <button className="btn-icon" title={t('common.delete')} aria-label={t('users.deleteAria', { name: org.name })} onClick={() => handleDelete(org)}>🗑️</button>
                </td>
              </tr>
              {expandedOrg === org.id && (
                <tr key={`${org.id}-stats`} className="org-stats-row">
                  <td colSpan={9}>
                    {orgStats[org.id] ? (
                      <div className="org-stats-detail">
                        <div className="org-stat-item">
                          <span className="org-stat-value">{fmt.number(orgStats[org.id].userCount)}</span>
                          <span className="org-stat-label">{t('orgs.statUsers')}</span>
                        </div>
                        <div className="org-stat-item">
                          <span className="org-stat-value">{fmt.number(orgStats[org.id].merchantCount)}</span>
                          <span className="org-stat-label">{t('orgs.statMerchants')}</span>
                        </div>
                        <div className="org-stat-item">
                          <span className="org-stat-value">{fmt.number(orgStats[org.id].advertiserCount)}</span>
                          <span className="org-stat-label">{t('orgs.statAdvertisers')}</span>
                        </div>
                        <div className="org-stat-item">
                          <span className="org-stat-value">{fmt.number(orgStats[org.id].crawlReportCount)}</span>
                          <span className="org-stat-label">{t('orgs.statReports')}</span>
                        </div>
                        <div className="org-stat-item">
                          <span className="org-stat-value">{fmt.number(orgStats[org.id].scanCount)}</span>
                          <span className="org-stat-label">{t('orgs.statScans')}</span>
                        </div>
                      </div>
                    ) : (
                      <p>{t('orgs.statsLoading')}</p>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {orgs.length === 0 && (
            <tr><td colSpan={9} className="table-empty">{t('orgs.empty')}</td></tr>
          )}
        </tbody>
      </table>
      </div>
    </div>
  )
}
